import { Directive, HostListener, Input } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { StackingPlanService } from '../../../stacking-plan.service';
import { SettingsService } from '../settings/settings.service';
import { Executable } from '../executable';
import { ExportExecutableService } from './export-executable.service';
import { LocalizationOptionsService } from '../../../localization-option.service';
import { FloorContainerRepository } from '../../floor-container/floor-container-repository.service';
import { ElevatorBanksRepository } from '../../floor-container/elevator-banks/elevator-banks-repository.service';
import { Observable, filter, map, of, switchMap } from 'rxjs';
import { ExportComponent } from './export.component';
import { FileType, StackingPlanExportTypes } from './export.model';
import { NotificationService } from '../../../shared/services/notification.service';
import { ConfirmDialogSPComponent } from '../../dialogs/confirm-dialog-sp/confirm-dialog-sp.component';

/**
 * Represents a directive that handles the export functionality.
 */
@UntilDestroy()
@Directive({
  selector: '[spExport]',
  providers: [ExportExecutableService],
  standalone: false,
})
export class ExportExecutableDirective implements Executable {
  /**
   * The export type to be used needs to be defined if not using the dialog
   * by default it will use the SPExcel
   */
  @Input() spExportType = StackingPlanExportTypes.SPExcel;
  /**
   * The export file type to be used needs to be defined
   */
  @Input() spExportFileType: FileType;
  /**
   * If the dialog should be shown or not
   */
  @Input() spShowDialog = true;
  /**
   * If the redact dialog should be shown or not
   */
  @Input() spCanRedact = false;

  constructor(
    private exportService: ExportExecutableService,
    private settingsService: SettingsService,
    private stackingPlanService: StackingPlanService,
    private floorDataRepository: FloorContainerRepository,
    private elevatorBanksRepository: ElevatorBanksRepository,
    private localizationOptions: LocalizationOptionsService,
    private notificationService: NotificationService
  ) {}

  /**
   * Open the export dialog. On close and if the user clicked on export, it
   * requests to get the exported data with the selected configuration
   */
  @HostListener('click', ['$event'])
  execute<Event>(_: Event): void {
    let handleExport$ = of({});

    if (this.spShowDialog) {
      handleExport$ = this.#openDialog();
    } else if (this.spCanRedact) {
      handleExport$ = this.#openRedactDialog().pipe(
        map(resp => {
          if (resp === true) {
            return { user_conf: '0' };
          } else if (resp === false) {
            return true;
          }
          return null;
        })
      );
    }

    handleExport$
      .pipe(
        filter(Boolean),
        switchMap(dialogOptions =>
          this.#executeExportServiceParams(dialogOptions as Record<string, any> & { fileType: string })
        )
      )
      .subscribe();
  }

  /**
   * Get the exportOptions with the SP configurations and data
   * @returns the export options for the dialog
   */
  #getExportOptions() {
    return {
      exportSettings: this.settingsService.getSettings() as never,
      floorElevatorData: {
        floorInfo: this.floorDataRepository.floorData,
        elevatorInfo: this.elevatorBanksRepository.elevatorBanks,
        elevatorInfoProcessed: this.elevatorBanksRepository.elevatorBanksMatrix,
      },
      localeId: this.localizationOptions.getInstance().localeId,
    };
  }

  /**
   * Execute the export service with the selected configuration
   * @param dialogOptions
   * @returns {Observable<any>}
   */
  #executeExportServiceParams(dialogOptions: Record<string, any> & { fileType: string }): Observable<any> {
    return this.exportService.execute({
      exportOptions: this.#getExportOptions(),
      dialogOptions,
      fileType: (dialogOptions.fileType || this.spExportFileType) as FileType,
      exportType: dialogOptions.exportType || this.spExportType,
      propertyId: this.stackingPlanService.propertyId,
      propertyData: this.stackingPlanService.propertyData,
    });
  }

  /**
   * Opens the dialog with the available export options
   * @returns {Observable<any>}
   */
  #openDialog(): Observable<any> {
    return this.notificationService.openDialog(ExportComponent, this.#getExportOptions(), {
      panelClass: 'alert-modals',
      autoFocus: false,
    });
  }

  #openRedactDialog(): Observable<any> {
    return this.notificationService.openDialog(
      ConfirmDialogSPComponent,
      {
        message: 'exportAndSettings.redact.message',
        confirmText: 'exportAndSettings.redact.cancelText',
        cancelText: 'exportAndSettings.redact.confirmText',
        showCloseButton: true,
        title: 'exportAndSettings.redact.title',
      },
      {
        maxWidth: '43vw',
        position: { top: '10px' },
      }
    );
  }
}
